import React, { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faScrewdriverWrench, faDiceD20 } from "@fortawesome/free-solid-svg-icons";

/* ---- odds helpers ------------------------------------------------------- */

function americanToProbability(odds) {
  return odds > 0 ? 100 / (odds + 100) : -odds / (-odds + 100);
}

function probabilityToAmerican(probability) {
  const raw =
    probability >= 0.5
      ? (-100 * probability) / (1 - probability)
      : (100 * (1 - probability)) / probability;
  const rounded = Math.round(raw);
  return rounded > 0 ? `+${rounded}` : String(rounded);
}

function parseOdds(value) {
  const cleaned = String(value).trim().replace(/\s/g, "");
  const odds = Number(cleaned);
  if (!Number.isFinite(odds) || odds === 0 || (odds > -100 && odds < 100)) {
    return { valid: false, odds: null };
  }
  return { valid: true, odds };
}

/* ---- BO3 converter ------------------------------------------------------ */

function ResultCard({ label, value }) {
  return (
    <div className="flex flex-col gap-1.5 rounded-xl border border-slate-200 bg-primary/5 p-4">
      <span className="text-xs font-medium text-slate-500">{label}</span>
      <strong className="font-mono text-2xl font-semibold text-slate-900">{value}</strong>
    </div>
  );
}

function Bo3OddsConverter() {
  const [raw, setRaw] = useState("-127");

  const { valid, odds } = useMemo(() => parseOdds(raw), [raw]);

  const results = useMemo(() => {
    if (!valid) return null;
    const p = americanToProbability(odds);
    return {
      mapProb: p,
      series: probabilityToAmerican(p * p * (3 - 2 * p)), // win the BO3
      sweep: probabilityToAmerican(p * p), // -1.5 maps (2-0)
      winMap: probabilityToAmerican(1 - Math.pow(1 - p, 2)), // +1.5 maps (win a map)
    };
  }, [valid, odds]);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center gap-2">
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <FontAwesomeIcon icon={faDiceD20} />
        </span>
        <div>
          <h3 className="text-sm font-semibold text-slate-900">BO3 Odds Converter</h3>
          <p className="text-xs text-slate-400">Map 1 price → best-of-three series prices</p>
        </div>
      </div>

      <label className="mt-4 flex flex-col gap-1.5" htmlFor="bo3-map-line">
        <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
          Fair Map 1 American odds
        </span>
        <input
          id="bo3-map-line"
          type="text"
          inputMode="numeric"
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          placeholder="-127"
          className="form-control max-w-[220px] font-mono"
        />
      </label>

      {!valid ? (
        <p className="mt-2 text-xs font-medium text-rose-500">
          Enter valid American odds, such as -127 or +225.
        </p>
      ) : (
        <p className="mt-2 text-xs text-slate-400">
          Implied Map 1 win probability:{" "}
          <span className="font-semibold text-slate-600">
            {(results.mapProb * 100).toFixed(1)}%
          </span>
        </p>
      )}

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <ResultCard label="BO3 series ML" value={results ? results.series : "—"} />
        <ResultCard label="−1.5 maps (2–0)" value={results ? results.sweep : "—"} />
        <ResultCard label="+1.5 maps (win a map)" value={results ? results.winMap : "—"} />
      </div>

      <p className="mt-3 text-xs text-slate-400">
        Assumes identical, independent win probability on every map.
      </p>
    </div>
  );
}

/* ---- page --------------------------------------------------------------- */

export default function Tools() {
  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-slate-400">Utilities</p>
          <div className="flex items-center gap-3">
            <h1>Tools</h1>
            <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-500">
              <FontAwesomeIcon icon={faScrewdriverWrench} className="text-[11px]" />
              Quick calculators
            </span>
          </div>
        </div>
      </div>

      <hr />

      <div className="my-4 grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Bo3OddsConverter />
      </div>
    </>
  );
}
